import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import cx from 'classnames'
import isEqual from 'lodash/isEqual'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { BtnV2, l } from '@manychat/manyui'

import { SubmitRef } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { useFlowPicker } from 'apps/flowPicker/hooks/useFlowPicker'
import { FlowPickerSource } from 'apps/flowPicker/lib/constants'
import { FlowPickerActions } from 'apps/flowPicker/store'
import { fetchFlow } from 'common/flow/actions/flowActions'
import * as flowSelectors from 'common/flow/selectors/flowSelectors'
import { ExitActionType, LeadQualExitAction } from 'shared/api/requests/aiEngagementGoal/schemas'

import { ExitActionProps } from '../../lib/types'

import cm from './StartFlowAction.module.css'

export const StartFlowAction = forwardRef<SubmitRef<LeadQualExitAction>, ExitActionProps>(
  ({ exitActionData, isActive }: ExitActionProps, ref) => {
    const dispatch = useAppDispatch()

    const [flowNs, setFlowNs] = useState<string | null>(
      exitActionData && exitActionData.type === ExitActionType.START_FLOW
        ? exitActionData.data.flow_ns
        : null,
    )
    const flow = useAppSelector((state) => flowSelectors.getById(state, flowNs))
    const [hasError, setHasError] = useState(false)

    const handleOpenFlowPicker = () => {
      if (!isActive) {
        return
      }

      dispatch(FlowPickerActions.openModal({ source: FlowPickerSource.AI_AUTOMATION }))
    }

    useFlowPicker({
      source: FlowPickerSource.AI_AUTOMATION,
      onFlowSelect: (flowId) => {
        setFlowNs(flowId)
      },
    })

    useEffect(() => {
      if (flowNs) {
        dispatch(fetchFlow(flowNs))
      }
    }, [dispatch, flowNs])

    useEffect(() => {
      if (!exitActionData || exitActionData.type !== ExitActionType.START_FLOW) {
        dispatch(AiAutomationLayoutActions.showConfirmChanged(isActive))
        return
      }

      if (isActive) {
        const hasChanges = !isEqual(exitActionData.data.flow_ns, flowNs)
        dispatch(AiAutomationLayoutActions.showConfirmChanged(hasChanges))
      } else {
        dispatch(AiAutomationLayoutActions.showConfirmChanged(false))
      }
    }, [dispatch, exitActionData, flowNs, isActive])

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          if (!flowNs) {
            setHasError(true)
            return { isValid: false }
          }

          return {
            isValid: true,
            data: {
              type: ExitActionType.START_FLOW,
              data: {
                flow_ns: flowNs,
              },
            },
          }
        },
      }),
      [flowNs],
    )

    return (
      <div>
        <div className="d-flex justify-start align-center gap-xs">
          <p className={cx('font-semibold text-nowrap', !isActive && 'text-secondary')}>
            {l.translate('<l10n context="AI will trigger an automation"/>AI will trigger')}
          </p>

          <button
            className={cx(
              cm.selectFlowButton,
              !isActive && cm.selectFlowButton__disabled,
              hasError && 'text-danger',
            )}
            onClick={handleOpenFlowPicker}
          >
            {flow ? flow.name : l.translate('Select an automation')}
          </button>

          {flow && isActive && (
            <BtnV2
              onClick={() => setFlowNs(null)}
              className={cm.deleteButton}
              variant="ghost-light"
              size="small"
              onlyIcon="Close"
              ariaLabel={l.translate('Unselect an automation')}
            />
          )}
        </div>

        {hasError && (
          <p className="text-danger text-sm">{l.translate('Select an automation to continue')}</p>
        )}
      </div>
    )
  },
)
